import type { IndexableType, Table } from 'dexie';
import { SvelteMap } from 'svelte/reactivity';
import { db, dexie_available } from '../localdb/db';
import { decrypt_row, encrypt_row } from '../localdb/encrypt';
import { create_live_query, create_view, type ScopedView } from '../localdb/live_query.svelte';
import { descriptor_to_collection, keyset_count, keyset_page, type WhereDescriptor } from '../localdb/keyset';
import { create_syncer, type Syncer } from '../sync/syncer';
import { register_managed_store } from './store_lifecycle';

export type StoreConfig<T extends { id: string }> = {
	name: string;
	keys?: { last_updated?: string; main?: string };
	fetch?: {
		many?: (opts: { where?: unknown }) => Promise<T[] | null>;
		one?: (id: string) => Promise<T | null>;
		aggregate?: () => Promise<{ count: number } | null>;
		ids?: () => Promise<{ id: string }[] | null>;
	};
	stream?: (
		max: string | null
	) => (on_batch: (rows: T[]) => void) => (() => void) | void;
	mutate?: {
		create?: (input: any) => Promise<T | null>;
		update?: (input: any) => Promise<T | null>;
		delete?: (id: string) => Promise<string | null>;
	};
	merge_response?: (cached: T, response: any) => T;
	auto?: { populate?: boolean; subscribe?: boolean; prune?: boolean };
	derive?: (row: T) => T;
	encrypt_fields?: string[];
	refetch_on_update?: boolean;
	/**
	 * H5 boundary (documented, not enforced): every view filter must be
	 * index-backed, or the store config justifies the scan. `scan_budget`
	 * is the declared ceiling for JS-filter scans on this store — set it
	 * when a filter cannot be expressed as an index descriptor.
	 */
	scan_budget?: number;
};

export type Store<T extends { id: string }> = {
	get: (id: string) => { value: T | undefined; error: unknown; loading: boolean };
	count: number;
	where: (descriptor: WhereDescriptor) => ScopedView<T>;
	paginated_view: (options: {
		index: string;
		sort?: 'asc' | 'desc';
		page_size?: number;
		filter?: (row: T) => boolean;
	}) => {
		values: T[];
		count: number;
		page: number;
		ready: boolean;
		error: unknown;
		promise: Promise<void> | null;
		next: () => void;
		prev: () => void;
		goto_page: (page: number) => void;
	};
	materialize: () => Promise<T[]>;
	mutate: {
		create: (input: any) => Promise<T | null>;
		update: (input: any) => Promise<T | null>;
		delete: (id: string) => Promise<boolean>;
	};
	on_change: (fn: (rows: T[]) => void) => () => void;
	on_delete: (fn: (ids: string[]) => void) => () => void;
	sync: Syncer<T> & { start: () => Promise<void> };
	reset: () => Promise<void>;
};

/**
 * Server-safe stubs: when dexie_available is false there is no Dexie object,
 * so every store method that would touch a table at call time returns an
 * empty, ready, error-free view instead (no MissingAPIError on SSR).
 */
function empty_view<T extends { id: string }>(): ScopedView<T> {
	const map = new SvelteMap<string, T>();
	return {
		get values() {
			return [];
		},
		get map() {
			return map;
		},
		get ready() {
			return true;
		},
		get error() {
			return undefined;
		},
		get promise() {
			return null;
		},
		get last_rows() {
			return [];
		}
	};
}

function empty_get_result<T>(): { value: T | undefined; error: unknown; loading: boolean } {
	return {
		get value() {
			return undefined;
		},
		get error() {
			return undefined;
		},
		get loading() {
			return false;
		}
	};
}

function empty_paginated_view<T extends { id: string }>(): ReturnType<Store<T>['paginated_view']> {
	return {
		get values() {
			return [];
		},
		get count() {
			return 0;
		},
		get page() {
			return 1;
		},
		get ready() {
			return true;
		},
		get error() {
			return undefined;
		},
		get promise() {
			return null;
		},
		next: () => {},
		prev: () => {},
		goto_page: () => {}
	};
}

export function create_store<T extends { id: string }>(config: StoreConfig<T>): Store<T> {
	// Server-safe: no Dexie instance exists when dexie_available is false;
	// every method that touches `table` at call time guards on the flag first.
	const table = (dexie_available ? db.table(config.name) : null) as unknown as Table<T, string>;
	const encrypt_fields: readonly string[] = config.encrypt_fields ?? [];
	const auto = {
		populate: config.auto?.populate ?? true,
		subscribe: config.auto?.subscribe ?? true,
		prune: config.auto?.prune ?? true
	};

	const change_subscribers = new Set<(rows: T[]) => void>();
	const delete_subscribers = new Set<(ids: string[]) => void>();

	function emit_change(rows: T[]) {
		for (const fn of change_subscribers) fn(rows);
	}

	function emit_delete(ids: string[]) {
		for (const fn of delete_subscribers) fn(ids);
	}

	function on_change(fn: (rows: T[]) => void): () => void {
		change_subscribers.add(fn);
		return () => change_subscribers.delete(fn);
	}

	function on_delete(fn: (ids: string[]) => void): () => void {
		delete_subscribers.add(fn);
		return () => delete_subscribers.delete(fn);
	}

	async function persist(rows: T[]): Promise<void> {
		if (!dexie_available) return;
		const plain = rows.map((row) => $state.snapshot(row) as T);
		const derived = plain.map((row) => (config.derive ? config.derive(row) : row));
		const encrypted = await Promise.all(derived.map((row) => encrypt_row(row, encrypt_fields)));
		await table.bulkPut(encrypted);
		emit_change(derived);
	}

	const count_query = create_live_query<number>(() => table.count());

	const get_cache = new Map<string, ReturnType<typeof make_get>>();

	// H2: pure liveQuery read. No speculative auto-refetch on miss — the
	// miss-fetch contract is designed at the first production caller.
	function make_get(id: string) {
		return create_live_query<T | undefined>(
			async () => {
				const row = await table.get(id);
				return row ? await decrypt_row(row, encrypt_fields) : undefined;
			}
		);
	}

	function get(id: string) {
		if (!dexie_available) return empty_get_result<T>();
		let entry = get_cache.get(id);
		if (!entry) {
			entry = make_get(id);
			get_cache.set(id, entry);
		}
		return entry;
	}

	const view_cache = new Map<string, ScopedView<T>>();

	// A1: JSON.stringify drops function props, so two filtered views on the
	// same store would silently share one cached view (first caller wins).
	// Key by filter identity — distinct closures (even identical source) get
	// distinct ids. Bounded in practice: views are module-level singletons.
	const filter_ids = new Map<Function, number>();
	let next_filter_id = 0;
	function filter_id(fn?: Function): string {
		if (!fn) return '';
		let id = filter_ids.get(fn);
		if (id === undefined) {
			id = next_filter_id++;
			filter_ids.set(fn, id);
		}
		return String(id);
	}

	function where(descriptor: WhereDescriptor): ScopedView<T> {
		if (!dexie_available) return empty_view<T>();
		const { filter, ...rest } = descriptor;
		const key = JSON.stringify(rest) + `|filter:${filter_id(filter)}`;
		let view = view_cache.get(key);
		if (!view) {
			const { collection, post_filter } = descriptor_to_collection<T>(table, descriptor);
			view = create_view<T>({
				query: async () => {
					const rows = post_filter
						? await collection.filter(post_filter).toArray()
						: await collection.toArray();
					return Promise.all(rows.map((row) => decrypt_row(row, encrypt_fields)));
				},
				get_key: () => 'fixed'
			});
			view_cache.set(key, view);
		}
		return view;
	}

	const paginated_cache = new Map<string, ReturnType<typeof make_paginated_view>>();

	// H4: paginated_view — load-as-needed reactive pagination over Dexie indexes.
	// Each call returns a reactive { values, count, page, next(), prev(), goto_page() }
	// backed by a keyset cursor. The view is cached; callers receive the same reactive
	// object for identical { index, sort, page_size } (filter closures are key-separate).
	function paginated_view(options: {
		index: string;
		sort?: 'asc' | 'desc';
		page_size?: number;
		filter?: (row: T) => boolean;
	}) {
		if (!dexie_available) return empty_paginated_view<T>();
		const { index, sort = 'desc', page_size = 10, filter } = options;
		const key = JSON.stringify({ index, sort, page_size }) + `|filter:${filter_id(filter)}`;
		let cached = paginated_cache.get(key);
		if (!cached) {
			cached = make_paginated_view({ index, sort, page_size, filter });
			paginated_cache.set(key, cached);
		}
		return cached;
	}

	function make_paginated_view(options: {
		index: string;
		sort?: 'asc' | 'desc';
		page_size?: number;
		filter?: (row: T) => boolean;
	}) {
		const { index, sort = 'desc', page_size = 10, filter } = options;
		const cursor = $state<{
			page: number;
			before: IndexableType | undefined;
			after: IndexableType | undefined;
			skip: number;
		}>({
			page: 1,
			before: undefined,
			after: undefined,
			skip: 0
		});

		const view = create_view<T>({
			query: async () => {
				const c = cursor;
				const rows = await keyset_page(table, {
					index,
					sort,
					page_size,
					before: c.before,
					after: c.after,
					filter,
					skip: c.skip
				});
				return Promise.all(rows.map((row) => decrypt_row(row, encrypt_fields)));
			},
			get_key: () =>
				JSON.stringify([cursor.page, cursor.before ?? null, cursor.after ?? null, cursor.skip])
		});

		const count_query = create_live_query<number>(() => keyset_count(table, { filter }));

		function next() {
			const rows = view.last_rows;
			if (!rows.length) return;
			const last_row = rows[rows.length - 1];
			const anchor = (last_row as any)[index] as IndexableType;
			cursor.page += 1;
			cursor.skip = 0;
			cursor.before = undefined;
			cursor.after = undefined;
			if (sort === 'desc') cursor.before = anchor;
			else cursor.after = anchor;
		}

		async function prev() {
			const rows = view.last_rows;
			if (!rows.length) return;
			if (cursor.page <= 1) return;
			const first_row = rows[0];
			const anchor = (first_row as any)[index] as IndexableType;
			let skip = 0;
			if (sort === 'desc') {
				const above_count = await table.where(index).above(anchor).count();
				skip = Math.max(0, above_count - page_size);
				cursor.after = anchor;
				cursor.before = undefined;
			} else {
				const below_count = await table.where(index).below(anchor).count();
				skip = Math.max(0, below_count - page_size);
				cursor.before = anchor;
				cursor.after = undefined;
			}
			cursor.page -= 1;
			cursor.skip = skip;
		}

		function goto_page(page: number) {
			const target = Math.max(1, page);
			cursor.page = target;
			cursor.before = undefined;
			cursor.after = undefined;
			cursor.skip = target === 1 ? 0 : (target - 1) * page_size;
		}

		return {
			get values() {
				return view.values;
			},
			get count() {
				return count_query.value ?? 0;
			},
			get page() {
				return cursor.page;
			},
			get ready() {
				return view.ready;
			},
			get error() {
				return view.error;
			},
			get promise() {
				return view.promise;
			},
			next,
			prev,
			goto_page
		};
	}

	// H5: materialize() — explicit whole-store opt-in. Loads ALL rows from the Dexie
	// table into memory. This is the ONLY path that holds a full store in the JS heap.
	// Use only when a screen proves it needs all rows; prefer paginated_view or where().
	async function materialize(): Promise<T[]> {
		if (!dexie_available) return [];
		const rows = await table.toArray();
		return Promise.all(rows.map((row) => decrypt_row(row, encrypt_fields)));
	}

	async function mutate_create(input: any): Promise<T | null> {
		if (!dexie_available) return null;
		if (!config.mutate?.create) return null;
		const response = await config.mutate.create(input);
		if (!response) return null;
		await persist([response]);
		return response;
	}

	async function mutate_update(input: any): Promise<T | null> {
		if (!dexie_available) return null;
		if (!config.mutate?.update) return null;
		const response = await config.mutate.update(input);
		if (!response) return null;

		if (config.merge_response) {
			const target_id =
				response.id ?? input?.id ?? input?.pk_columns?.id;
			const cached = target_id ? await table.get(target_id) : undefined;
			if (cached) {
				const merged = config.merge_response(await decrypt_row(cached, encrypt_fields), response);
				await persist([merged]);
			} else if (response.id) {
				await persist([response]);
			}
		} else {
			await persist([response]);
		}

		const refetch_id = response.id ?? input?.pk_columns?.id;
		if (config.refetch_on_update && refetch_id) void syncer.refetch(refetch_id);
		return response;
	}

	async function mutate_delete(id: string): Promise<boolean> {
		if (!dexie_available) return false;
		if (!config.mutate?.delete) return false;
		const deleted_id = await config.mutate.delete(id);
		if (!deleted_id) return false;
		await table.delete(id);
		emit_delete([id]);
		return true;
	}

	const syncer = create_syncer<T>({
		name: config.name,
		table: () => table,
		fetch: config.fetch,
		stream: config.stream,
		last_updated_key: config.keys?.last_updated,
		persist,
		on_delete: (ids) => emit_delete(ids)
	});

	const sync: Syncer<T> & { start: () => Promise<void> } = {
		...syncer,
		start: async () => {
			// A5: when both populate and subscribe are on, delegate to
			// syncer.start() — it reads local_max ONCE and anchors populate +
			// stream at the same cursor (the split-anchor version could drift
			// if data changed between the two local_max reads).
			if (auto.populate && auto.subscribe) {
				await syncer.start();
			} else {
				if (auto.populate) await syncer.populate();
				if (auto.subscribe) syncer.subscribe();
				if (auto.prune) void syncer.prune();
			}
		}
	};

	async function reset(): Promise<void> {
		await syncer.reset();
	}

	// C2: managed stores self-register so the auth layer can start/refresh/reset
	// them without each feature wiring its own lifecycle hooks.
	if (auto.populate || auto.subscribe) {
		register_managed_store(config.name, {
			start: () => sync.start(),
			refresh: () => syncer.populate(),
			reset: () => reset()
		});
	}

	return {
		get,
		get count() {
			return dexie_available ? (count_query.value ?? 0) : 0;
		},
		where,
		paginated_view,
		materialize,
		mutate: {
			create: mutate_create,
			update: mutate_update,
			delete: mutate_delete
		},
		on_change,
		on_delete,
		sync,
		reset
	};
}